import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useTheme } from '../context/ThemeContext';
import './Login.css';
import axios from 'axios';

const Login = () => {
  const baseurl = process.env.REACT_APP_API_URL;
  const { isDark, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${baseurl}/login`, {
        user_id: email,
        password,
      });

      const { token } = response.data;
      if (token) {
        localStorage.setItem('token', token);
      }
      navigate('/dashboard');
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        'Authentication failed. Please verify your User ID and Password.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-wrapper">
      {/* Background Animated Orbs */}
      <div className="login-ambient-background">
        <div className="ambient-orb orb-1"></div>
        <div className="ambient-orb orb-2"></div>
        <div className="ambient-orb orb-3"></div>
      </div>

      {/* Theme toggle corner button */}
      <button
        className="login-theme-toggle"
        onClick={toggleTheme}
        title="Toggle Theme"
        aria-label="Toggle dark/light theme"
      >
        {isDark ? '☀️ Light' : '🌙 Dark'}
      </button>

      <Container className="login-main-container">
        <div className="login-glass-card">
          {/* Left Brand Panel */}
          <div className="login-brand-panel">
            <div className="brand-badge-pill">
              <span>🚀 Leader Portal</span>
            </div>
            <h1 className="brand-hero-title">
              AWPL Work <br />
              <span className="gradient-text">Reporting System</span>
            </h1>
            <p className="brand-hero-desc">
              Next-generation activity management, cheque tracking, and performance analytics for leaders.
            </p>

            <div className="brand-feature-list">
              <div className="feature-item">
                <span className="feature-icon">⚡</span>
                <div className="feature-text">
                  <strong>Instant Daily Reports</strong>
                  <span>Fast and reliable multi-field submissions</span>
                </div>
              </div>
              <div className="feature-item">
                <span className="feature-icon">💳</span>
                <div className="feature-text">
                  <strong>Cheque & Bonus Tracking</strong>
                  <span>Complete lifecycle audit and analytics</span>
                </div>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🔒</span>
                <div className="feature-text">
                  <strong>Secure & Protected</strong>
                  <span>Encrypted JWT-authenticated leader access</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form Panel */}
          <div className="login-form-panel">
            <div className="form-header-block">
              <div className="mobile-logo-badge">🚀 AWPL</div>
              <h2 className="form-title">Leader Sign In</h2>
              <p className="form-subtitle">Enter your leader credentials to access your dashboard</p>
            </div>

            {error && (
              <Alert variant="danger" className="modern-alert-danger">
                <span className="alert-icon">⚠️</span>
                <span>{error}</span>
              </Alert>
            )}

            <Form onSubmit={handleSubmit} className="modern-auth-form">
              <Form.Group className="mb-3">
                <Form.Label className="custom-form-label">Leader User ID</Form.Label>
                <div className="input-with-icon">
                  <span className="input-adornment">👤</span>
                  <Form.Control
                    type="text"
                    placeholder="Enter your User ID"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="custom-form-input"
                    autoComplete="username"
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Form.Label className="custom-form-label mb-0">Password</Form.Label>
                </div>
                <div className="input-with-icon">
                  <span className="input-adornment">🔒</span>
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="custom-form-input"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="password-reveal-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                    tabIndex="-1"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </Form.Group>

              <Button
                type="submit"
                className="btn-submit-auth"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <span>Authenticate Account</span>
                    <span className="btn-arrow-icon">→</span>
                  </>
                )}
              </Button>
            </Form>

            <div className="form-footer-meta">
              <span>Secure Connection • Version 1.0.0</span>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Login;